/* FILE: assets/sim_hero.js */

(() => {
  const SELECTOR = "[data-sim-hero]";
  const MEDIA_SELECTOR = "[data-sim-hero-media]";
  const CONTAINER_SELECTOR = ".sim-hero__container";

  const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
  }

  function parseBool(v) {
    return String(v) === "true";
  }

  function parseNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  // Nice, but crucially: DOES NOT create momentum by itself.
  function easeSineOut(p) {
    return Math.sin((p * Math.PI) / 2);
  }

  function easeOutQuad(p) {
    return 1 - (1 - p) * (1 - p);
  }

  function applyCurve(p, curve) {
    if (curve === "linear") return p;
    if (curve === "ease_out") return easeOutQuad(p);
    // default to sine_out feel (deceleration) without momentum tail
    return easeSineOut(p);
  }

  class SimHeroParallax {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector(CONTAINER_SELECTOR) || root;
      this.mediaInner = root.querySelector(MEDIA_SELECTOR);

      this.enabled = parseBool(root.dataset.parallaxEnabled);
      // this.intensity = parseNum(root.dataset.parallaxIntensity, 0);
      this.intensity = 100;

      this.curve = root.dataset.parallaxCurve || "ease_out";
      this.direction = root.dataset.parallaxDirection || "up";
      this.enableDesktop = parseBool(root.dataset.parallaxDesktop);
      this.enableMobile = parseBool(root.dataset.parallaxMobile);
      this.reduceMotionBehavior = root.dataset.reduceMotion || "force_disable";

      this.movable = null;

      // Scroll mapping bounds (computed rarely)
      this.startY = 0;
      this.endY = 1;

      // Range control
      this.maxShift = 0;
      this.scale = 1;

      // State
      this.inView = false;
      this.pending = false; // “needs update”
      this.raf = 0;

      // For very light “anti-jitter” without momentum:
      // We clamp per-frame delta so a single long scroll tick doesn’t jump.
      this.lastApplied = 0;

      // Observers
      this.io = null;

      this._onIntersect = this._onIntersect.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._applyFrame = this._applyFrame.bind(this);

      this._init();
    }

    _deviceAllowed() {
      const mobile = isMobileViewport();
      return mobile ? this.enableMobile : this.enableDesktop;
    }

    _effectiveEnabled() {
      if (!this.enabled) return false;

      if (prefersReducedMotion()) {
        return this.reduceMotionBehavior === "subtle";
      }

      return this._deviceAllowed();
    }

    _pickMovable() {
      if (!this.mediaInner) return null;
      return (
        this.mediaInner.querySelector("img.sim-hero__img") ||
        this.mediaInner.querySelector("svg.sim-hero__placeholder") ||
        this.mediaInner.querySelector(".sim-hero__placeholder") ||
        null
      );
    }

    _recomputeRanges() {
      const heroH = this.container?.clientHeight || this.root?.clientHeight || 0;
      if (heroH <= 0) {
        this.maxShift = 0;
        this.scale = 1;
        this.root.style.setProperty("--sim-hero-img-scale", "1");
        return;
      }

      const maxByHeight = heroH * .18;
      this.maxShift = clamp(this.intensity, 0, maxByHeight);

      const neededScale = 1 + (2 * this.maxShift) / heroH;
      this.scale = clamp(neededScale, 1, 1.28);

      this.root.style.setProperty("--sim-hero-img-scale", String(this.scale));
    }

    _recomputeScrollBounds() {
      // One layout read only on enter/resize/load
      const rect = this.root.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;

      const topDoc = rect.top + scrollY;
      const h = rect.height || this.root.clientHeight || 1;
      const vh = window.innerHeight || 1;

      this.startY = topDoc - vh;
      this.endY = topDoc + h;
      if (this.endY <= this.startY) this.endY = this.startY + 1;
    }

    _progress(scrollY) {
      const p = (scrollY - this.startY) / (this.endY - this.startY);
      return clamp(p, 0, 1);
    }

    _progressToOffsetCentered(progress01) {
      let centered = (0.5 - progress01) * 2; // +1..-1
      if (this.direction === "down") centered = -centered;
      return centered * this.maxShift;
    }

    _applyTransform(yPx) {
      if (!this.movable) return;
      this.movable.style.transform = `translate3d(0, ${yPx.toFixed(
        2
      )}px, 0) scale(${this.scale})`;
    }

    _schedule() {
      if (this.pending) return;
      this.pending = true;
      this.raf = requestAnimationFrame(this._applyFrame);
    }

    _applyFrame() {
      this.pending = false;
      if (!this.inView) return;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      let p = this._progress(scrollY);

      if (prefersReducedMotion() && this.reduceMotionBehavior === "subtle") {
        p = 0.5 + (p - 0.5) * 0.2;
      }

      const eased = applyCurve(p, this.curve);
      const target = this._progressToOffsetCentered(eased);

      // Anti-jitter without “momentum”:
      // Clamp how far the image can move in a single frame.
      // This prevents sudden jumps from coarse wheel deltas, but does not
      // continue animating after scrolling stops.
      const maxStep = Math.max(3, this.maxShift * 0.22);
      const delta = clamp(target - this.lastApplied, -maxStep, maxStep);
      const next = this.lastApplied + delta;

      this.lastApplied = next;
      this._applyTransform(next);
    }

    _onScroll() {
      // One paint per animation frame, no tail
      this._schedule();
    }

    _onResize() {
      if (!this.inView) return;
      this._recomputeRanges();
      this._recomputeScrollBounds();
      this._schedule();
    }

    _onIntersect(entries) {
      const entry = entries && entries[0];
      if (!entry) return;

      const enabled = this._effectiveEnabled();

      if (!entry.isIntersecting || !enabled) {
        this.inView = false;
        window.removeEventListener("scroll", this._onScroll, { passive: true });

        // Reset to neutral when out of view
        this.lastApplied = 0;
        this._applyTransform(0);
        return;
      }

      this.inView = true;

      // Recompute once when becoming relevant
      this._recomputeRanges();
      this._recomputeScrollBounds();

      window.addEventListener("scroll", this._onScroll, { passive: true });

      // Apply immediately (one frame)
      this._schedule();
    }

    _init() {
      if (!this.mediaInner) return;

      this.movable = this._pickMovable();
      if (!this.movable) return;

      // initial (may refine on load/IO)
      this._recomputeRanges();
      this._applyTransform(0);

      try {
        this.io = new IntersectionObserver(this._onIntersect, {
          root: null,
          rootMargin: "200px 0px 200px 0px",
          threshold: [0, 0.01]
        });
        this.io.observe(this.root);
      } catch (e) {
        // IO fallback: always on, still light
        this.inView = true;
        this._recomputeRanges();
        this._recomputeScrollBounds();
        window.addEventListener("scroll", this._onScroll, { passive: true });
        this._schedule();
      }

      window.addEventListener("resize", this._onResize, { passive: true });

      // After assets settle (image height, fonts), refresh once
      window.addEventListener(
        "load",
        () => {
          if (!this.inView) return;
          this._recomputeRanges();
          this._recomputeScrollBounds();
          this._schedule();
        },
        { once: true }
      );
    }

    destroy() {
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      window.removeEventListener("resize", this._onResize);
      window.removeEventListener("scroll", this._onScroll, { passive: true });

      if (this.pending) {
        cancelAnimationFrame(this.raf);
        this.pending = false;
        this.raf = 0;
      }

      if (this.movable) this.movable.style.transform = "";
      this.root.style.setProperty("--sim-hero-img-scale", "1");
    }
  }

  const instances = new Map();

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new SimHeroParallax(root));
  }

  function unmount(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.destroy();
    instances.delete(root);
  }

  function scan(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan());
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", (e) => {
    if (!e.target) return;
    scan(e.target);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    if (!e.target) return;
    e.target.querySelectorAll(SELECTOR).forEach(unmount);
  });
})();
